import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HOLD_RX_FOLLOW_UP_HOURS, addHours } from '../../../../constants/task-lists';

export interface HoldFollowUpDialogData {
  notes?: string;
  expectedDate?: string | Date;
  followUpDate?: string | Date;
}

export interface HoldRxPopupResult {
  notes: string;
  expectedDate: Date;
  followUpDate: Date;
}

@Component({
  selector: 'app-hold-follow-up-dialog',
  template: `
    <h2 mat-dialog-title>HOLD / RX</h2>
    <mat-dialog-content>
      <p class="hold-follow-up-message">
        Enter any relevant notes and the expected RX / appointment date.
        Follow-up is calculated automatically as 48 hours later.
      </p>
      <mat-form-field appearance="fill" class="hold-follow-up-field">
        <mat-label>NOTES</mat-label>
        <textarea matInput rows="4" [formControl]="notes"></textarea>
      </mat-form-field>
      <mat-form-field appearance="fill" class="hold-follow-up-field">
        <mat-label>EXPECTED RX / APPOINTMENT DATE</mat-label>
        <input matInput [matDatepicker]="picker" [formControl]="expectedDate">
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker></mat-datepicker>
      </mat-form-field>
      <div class="hold-follow-up-calculated" *ngIf="followUpDate">
        Follow-Up Date: {{ followUpDate | date:'M/d/yy' }}
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="cancel()">CANCEL</button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="!expectedDate.value"
        (click)="confirm()"
      >
        SAVE
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .hold-follow-up-message {
        margin: 0 0 16px;
        font-size: 13px;
        line-height: 1.4;
      }
      .hold-follow-up-field {
        width: 100%;
      }
      .hold-follow-up-calculated {
        margin: 0 0 8px;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.04em;
      }
    `,
  ],
})
export class HoldFollowUpDialogComponent {
  notes = new FormControl('');
  expectedDate = new FormControl(null);

  constructor(
    public dialogRef: MatDialogRef<
      HoldFollowUpDialogComponent,
      HoldRxPopupResult | null
    >,
    @Inject(MAT_DIALOG_DATA) public data: HoldFollowUpDialogData
  ) {
    this.notes.setValue(String(data?.notes || ''));
    this.expectedDate.setValue(
      this.parseDate(data?.expectedDate) || this.backDateFromFollowUp(data?.followUpDate)
    );
  }

  get followUpDate(): Date | null {
    const expected = this.parseDate(this.expectedDate.value);
    return expected ? addHours(expected, HOLD_RX_FOLLOW_UP_HOURS) : null;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    const expected = this.parseDate(this.expectedDate.value);
    if (!expected) {
      return;
    }
    this.dialogRef.close({
      notes: String(this.notes.value || '').trim(),
      expectedDate: expected,
      followUpDate: addHours(expected, HOLD_RX_FOLLOW_UP_HOURS),
    });
  }

  private backDateFromFollowUp(value?: string | Date): Date | null {
    const followUp = this.parseDate(value);
    if (!followUp) {
      return null;
    }
    return addHours(followUp, -HOLD_RX_FOLLOW_UP_HOURS);
  }

  private parseDate(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
}
